// Enter's wait, shared by the jump bar (components/JumpBar.svelte) and the
// header pickers (components/RefPicker.svelte): Enter never takes a guess
// a source still asked may replace. On a guess it asks every debounced
// source at once and waits for their answers, then takes what is active;
// past FIND_MS with a source still asked it takes no guess, says it is
// still finding, and a second Enter takes the active row as it is. A new
// choice (typing, an arrow, a click) or closing (Esc) stops the wait.
import type { Schedule } from './complete';

/** How long Enter waits, after asking every source at once, for those that may replace a guess. */
export const FIND_MS = 1000;

export interface EnterWaitOptions {
  /** The wait's clock (the component's, so tests run it by hand). */
  schedule: Schedule;
  /** Whether the active row may be taken now: no source still asked may replace it. */
  sure: () => boolean;
  /** Ask the debounced sources now. */
  flush: () => void;
  /** Take the active row, as it is. */
  take: () => void;
  /** A wait started: what the region said before is no longer news (a pin), so the component forgets it. */
  onwait?: () => void;
  ms?: number;
}

export class EnterWait {
  /** Enter is waiting: the text it was pressed on, and whether it is past the cap (a second Enter takes the row as it is). */
  finding: { text: string; slow: boolean } | null = $state.raw(null);
  readonly #o: EnterWaitOptions;
  #cancel: (() => void) | null = null;

  constructor(o: EnterWaitOptions) {
    this.#o = o;
  }

  /** What a polite live region says of the wait; empty while there is none. */
  get said(): string {
    const f = this.finding;
    if (!f) return '';
    const t = f.text.trim();
    return f.slow ? `Still finding ${t}. Enter again opens it as typed; or choose a result.` : `Finding ${t}…`;
  }

  /** What the list's foot says of the wait, shortly; empty while there is none. */
  get foot(): string {
    const f = this.finding;
    if (!f) return '';
    const t = f.text.trim();
    return f.slow ? `Still finding ${t}: Enter again opens it as typed` : `Finding ${t}…`;
  }

  /** Enter, on the text `text`. */
  enter(text: string): void {
    if (this.finding?.slow) return this.#found();
    if (this.finding) return;
    if (this.#o.sure()) return this.#o.take();
    this.#o.flush();
    if (this.#o.sure()) return this.#o.take();
    this.finding = { text, slow: false };
    this.#o.onwait?.();
    this.#cancel = this.#o.schedule(() => {
      this.#cancel = null;
      if (!this.finding) return;
      if (this.#o.sure()) return this.#found();
      this.finding = { ...this.finding, slow: true };
    }, this.#o.ms ?? FIND_MS);
  }

  /** The sources changed: once none is asked, a waiting Enter takes what is active. */
  changed(settled: boolean): void {
    if (this.finding && settled) this.#found();
  }

  /** A new choice, or the field closed: no wait goes on. */
  stop(): void {
    this.#cancel?.();
    this.#cancel = null;
    this.finding = null;
  }

  #found() {
    this.stop();
    this.#o.take();
  }
}
